import React, { useState, useEffect } from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useUserContext } from 'common/contexts/UserContext';
import { useLocation } from 'react-router-dom';
import { getPage } from 'constants/pages';
import NotAvailable from 'pages/Mobile/NotAvailable';
import { useSegment } from 'common/hooks/segment';
import { PermissionGate } from 'utils/permissions/PermissionGate';
import TrialDialogs from './TrialDialogs';
import RequestToJoinCompany from './RequestToJoinCompany';
import { Banners } from './Banners';
import mediaQueries from '../../mediaqueries';

import { DesktopNav, DesktopPageTemplate } from './desktop';
import { MobileNav, MobilePageTemplate } from './mobile';
import { NavWrapper } from './NavWrapper';
import { RegionGate } from './RegionGate';

const AppPageTemplate: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const { user, fetchUser } = useUserContext();
  const location = useLocation();
  const tablet = useMediaQuery(`(max-width:${mediaQueries.md})`, {
    noSsr: true,
  });
  const isMobile = useMediaQuery(`(max-width:${mediaQueries.sm})`, {
    noSsr: true,
  });
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { trackPage } = useSegment();

  const handleMenuOpen = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  useEffect(() => {
    // Note this is not awaited
    fetchUser();

    // Get the information for this page to populate the title
    const page = getPage(location.pathname);
    document.title = (page && page.title) || 'BlueCargo';

    // Track the page view
    trackPage(location.pathname);
  }, [fetchUser, location.pathname, trackPage]);

  if (isMobile) {
    return <NotAvailable />;
  }

  const navigation = tablet ? (
    <>
      <MobileNav user={user} handleMenuOpen={handleMenuOpen} isMenuOpen={isMenuOpen} />
      <Banners />
    </>
  ) : (
    <DesktopNav user={user} />
  );

  const content = tablet ? (
    <MobilePageTemplate handleMenuOpen={handleMenuOpen} isMenuOpen={isMenuOpen}>
      {children}
    </MobilePageTemplate>
  ) : (
    <DesktopPageTemplate>{children}</DesktopPageTemplate>
  );

  return (
    <>
      <NavWrapper>{navigation}</NavWrapper>
      <RegionGate>
        <PermissionGate>{content}</PermissionGate>
      </RegionGate>
      <RequestToJoinCompany />
      <TrialDialogs />
    </>
  );
};
AppPageTemplate.displayName = 'AppPageTemplate';

export default AppPageTemplate;
